<template>
  <div class="blog">
    <div class="container">
      <div class="row">
        <div class="col-lg-8">
          <h2>{{ title }}</h2>
          <div>
             <img src="@/assets/images/assets/blog/rag_system.png" alt="RAG System Guide" />
          </div>
          <div v-html="sanitizedContent"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DOMPurify from 'dompurify';
import blogImg from '@/assets/images/assets/blog/rag_arch.png';
import blogImg2 from '@/assets/images/assets/blog/rag_chunking.png';
import blogImg3 from '@/assets/images/assets/blog/rag_ingestion.png';
import blogImg4 from '@/assets/images/assets/blog/rag_retrieval.png';
import blogImg5 from '@/assets/images/assets/ai_app_dark_with_bedrock.svg';

export default {
  name: 'BlogPostRagSystemGuide',
  data() {
    return {
      title: 'Simple Guide on how to build a RAG system (GenAI) aka “Talking with your PDF files”.',
      content: `

        <h3>1. Simple Introduction</h3>
                <p>Note: You can talk to the bot in the <b>bottom right corner</b>.</p>

        <p>------------------------</p>
        <p>

          <b>RAG</b> (Retrieval Augmented Generation) System is probably the most popular GenAI application, which can be simplified as:
          <b>“Upload PDF to ChatGPT and talk to them”</b>. This is of course not limited to PDF, this is just an example.
          Basically it is an application which searches your custom data and gives answers (with the help of LLM model) based on it.
        </p>
        <p>
          Another example is a chatbot based on your documentation, where your users, instead of searching documentation for their particular issue,
          get the answer from the chatbot (the search part is done on user's behalf).
        </p>


        <h3 class="mt-35">2. Inner workings</h3>
        <p>
          There are two main components: <b>vector database and LLM models</b>. Vector database is where the search for relevant information happens.
        </p>
         <div class="documentation-image">
          <img
            src="${blogImg}"
            alt="RAG System Guide"
            style="width: 70%; height: 70%; display: block; margin: 0 auto;"
          />
        </div>
        <p>LLM models are used:</p>
        <ul>
          <li>
            to convert your custom information in the form of the embeddings (vectors) so it can be saved into the vector database.
          </li>
          <li>
            to construct a response when vector search returns the answer to the question.
          </li>
        </ul>
        <p>There are main 2 steps:</p>
        <ul>
          <li><b>data ingestion into the vector database</b></li>
          <li><b>data retrieval</b></li>
        </ul>

        <h3 class="mt-25">2.1. Data ingestion into the vector database.</h3>
             <div class="documentation-image">
          <img
            src="${blogImg3}"
            alt="RAG System Guide"
            style="width: 90%; height: 90%; display: block; margin: 0 auto;"
          />
        </div>
        <p>
          Whatever the source (photos, PDFs, text files) in the end you need text (the photos can be described, the text from PDF can be extracted, etc).
          So first you convert your source files to text (can be any form of text, it can also be a markdown, for example).
        </p>
        <p>
          After that you use LLM model (the one that uses embeddings) to convert that text into vectors.
        </p>
        <p>
          Then you take those vectors and put them into a vector database.
        </p>

        <h3 class="mt-25">2.1.1. How to chunk your data.</h3>
        <p>
          In data ingestion part you usually will need to decide how to <b>chunk</b> your text data.
        </p>

       <div class="documentation-image">
          <img
            src="${blogImg2}"
            alt="RAG System Guide"
            style="width: 90%; height: 90%; display: block; margin: 0 auto;"
          />
        </div>
        <p class="mt-25">
          One of the options is that you chunk based on the amount of <b>characters</b> in each chunk, for example, one chunk is 1536 characters.
        </p>
        <p>
          Another option is to chunk based on <b>tags</b>. For example, if your text is markdown, you can chunk it based on the symbol #
          (commonly used in markdown to denote heading, sections, subsections, etc.), so your chunks will have similar structure as of your text.
          So when it is retrieved it will be, for example, a section from your documentation.
        </p>
        <p>
          You usually also add overlapping (meaning that each chunk contains some part of its neighboring chunks).
        </p>
        <p>
          By the way, you usually don't have to code this logic in detail, you usually just use a library from a LangChain family and just choose how to chunk.
        </p>

        <h3 class="mt-25">2.2. Data retrieval:</h4>
             <div class="documentation-image">
          <img
            src="${blogImg4}"
            alt="RAG System Guide"
            style="width: 70%; height: 70%; display: block; margin: 0 auto;"
          />
        </div>
        <p>
          The diagram displays the process.
        </p>


        <h3 class="mt-35">3. How to build this</h3>
        <p>
          Here I will list 3 options on how to build a RAG system, all of them can be setup pretty quickly (I also built and tried all of them), and link some documentation to it.
        </p>

        <h3 class="mt-15">Option 1: OpenAI assistant.</h3>
        <p>
          The easiest to set up and run.
        </p>
        <p>
          You upload your custom data to the assistant vector database (just drag and drop).
        </p>
        <p>
          You use OpenAI library to process the user questions on your backend.
        </p>
        <p>
          Everything else happens automatically.
        </p>
        <p>
          The benefit here is that vector database is truly serverless, meaning you don't pay when it is not run.
        </p>
<p>
  Use <a href="https://platform.openai.com/docs/assistants/overview" target="_blank">OpenAI Assistant documentation</a> to build this.
</p>


        <h3 class="mt-15">Option 2: AWS Bedrock.</h3>
        <p>
          If you use only AWS resources (for example, because of the policy of your organisation), it makes sense.
        </p>
        <p>
          AWS Bedrock provides access to LLM models, similarly to OpenAI.
        </p>
        <p>
          The choice here is which vector database to use. If you choose native OpenSearch Serverless, you will still pay 24/7 because it runs constantly.
          And it can be costly.
        </p>
        <p>
          But you can also use external vector databases, such as Pinecone, which is truly serverless (you don't pay if nobody uses it),
          and you can even choose to host it in one of AWS regions.
        </p>
 <p>
  <a href="https://docs.aws.amazon.com/bedrock/latest/userguide/what-is-bedrock.html" target="_blank">AWS Bedrock</a> has extensive documentation, follow to proceed.
  You will probably need to use the <a href="https://python.langchain.com/docs/integrations/llms/bedrock" target="_blank">LangChain Bedrock</a> library, here is the documentation as well.
</p>


<h3 class="mt-15">Option 3: Lance DB.</h3>
<p>
  LanceDB is a small vector database, which you can host anywhere, including AWS.
  For example, you can use your AWS Lambda and host your actual files in S3.
</p>
<p>
  When using LanceDB you can choose from a variety of LLM models and search strategies.
</p>
<p>
  LanceDB is fast, and hosting it is cheap. It is faster than Option 1 and 2, which is its main advantage.
</p>
<p>
  Here is some <a href="https://lancedb.github.io/lancedb/guides/storage" target="_blank">documentation</a>.
</p>
<p>
  It will require more work than Option 1 or Option 2, so if you’re just starting out, I'd go with Option 1 to see how things work and then switch to LanceDB once you get a hang of it.
</p>


        <h3 class="mt-25">4. Just use what I have already</h3>
            <div class="documentation-image">
              <a href="https://saasconstruct.com" target="_blank">
                <img
                  src="${blogImg5}"
                  alt="RAG System Guide"
                  style="width: 70%; height: 70%; display: block; margin: 0 auto;"
                />
              </a>
            </div>

        <p class="mt-25">
          If you want to have RAG fast and out-the-box, you can use a SaaS Template that I wrote:
          <a href="https://saasconstruct.com" target="_blank">https://saasconstruct.com</a>.
        </p>
        <p>
          It already has all the logic for Option 1 and Option 2, you just have to choose which one you want to use, with extensive documentation.
          Besides that, there are also tons of other features, like payments, authentication, user management, etc.
        </p>
 <p>
  Follow the <a href="https://saasconstruct.com/documentation/get-started" target="_blank">documentation</a> to set it up.
</p>

      `,
    };
  },
  computed: {
    sanitizedContent() {
      const config = { ADD_ATTR: ['target'] };
      return DOMPurify.sanitize(this.content, config);
    }
  }
};
</script>
